import React from 'react';
import { usePWA } from '../hooks/usePWA';
import { Button } from './ui/button';
import { 
  WifiOff, 
  RefreshCw, 
  Download,
  X,
  Wifi
} from 'lucide-react';

/**
 * PWA Status Bar - Shows offline status and update notifications
 */
export const PWAStatusBar = () => {
  const { 
    isOnline, 
    isUpdateAvailable, 
    isInstallable,
    updateServiceWorker,
    installPWA 
  } = usePWA();
  
  const [showInstallBanner, setShowInstallBanner] = React.useState(true);
  const [dismissed, setDismissed] = React.useState(false);

  // Don't show if dismissed
  if (dismissed && isOnline && !isUpdateAvailable) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg animate-slide-up"
          role="alert"
          data-testid="offline-banner"
        >
          <WifiOff className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm font-medium">
            Vous êtes hors ligne. Certaines fonctionnalités peuvent être limitées.
          </span>
        </div>
      )}

      {/* Online restored */}
      {isOnline && !dismissed && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-50 bg-emerald-500 text-white px-4 py-3 flex items-center justify-between shadow-lg animate-slide-up"
          style={{ animation: 'slideUp 0.3s ease-out, fadeOut 0.5s ease-out 2s forwards' }}
          onAnimationEnd={(e) => {
            if (e.animationName === 'fadeOut') {
              setDismissed(true);
            }
          }}
          data-testid="online-banner"
        >
          <div className="flex items-center gap-3">
            <Wifi className="w-5 h-5" />
            <span className="text-sm font-medium">Connexion rétablie</span>
          </div>
        </div>
      )}

      {/* Update Available Banner */}
      {isUpdateAvailable && (
        <div 
          className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-3 flex items-center justify-between shadow-lg"
          data-testid="update-banner"
        >
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5" />
            <span className="text-sm font-medium">
              Une nouvelle version est disponible
            </span>
          </div>
          <Button 
            size="sm" 
            variant="secondary"
            onClick={updateServiceWorker}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            Mettre à jour
          </Button>
        </div>
      )}

      {/* Install Banner */}
      {isInstallable && showInstallBanner && (
        <div 
          className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-40 bg-white rounded-xl shadow-2xl border p-4"
          data-testid="install-banner"
        >
          <button 
            onClick={() => setShowInstallBanner(false)}
            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-900 mb-1">
                Installer drbooking
              </h4>
              <p className="text-sm text-slate-500 mb-3">
                Accédez rapidement à l'application depuis votre écran d'accueil
              </p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={installPWA}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Installer
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setShowInstallBanner(false)}
                >
                  Plus tard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default PWAStatusBar;
